/**
 * Botón "Continuar con Google" — NER-3781.
 *
 * Signup/login 1-click vía el flujo OAuth *custom* de Google (services/social-auth,
 * NER-3785). Reemplaza el approach anterior de Cognito Hosted UI (CANCELLED:
 * NER-3782/3783). En vez de `signInWithRedirect` de Amplify, redirige el browser
 * al endpoint de initiate del backend, que arma la URL de consentimiento de
 * Google y firma el `state` (HMAC) para la rama admin:
 *
 *   GET {API_BASE}/auth/social/google/initiate?flow=admin&returnUrl={URL_ABSOLUTA}
 *
 * El backend valida que `returnUrl` sea una URL ABSOLUTA en un host first-party
 * (`*.nerdo.solutions` / localhost), así que construimos el returnUrl completo
 * hacia la ruta `/creating-org` (que respeta el basename `/resto/admin`), donde
 * `CreatingOrgPage` procesa el `#authToken`/`#authError` del fragment de retorno.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackPixel } from '../utils/analytics';

// API base del backend Resto (mismo valor que usa services/api.js). Inyectado
// por CI por entorno: dev/staging/prod → https://[dev.|staging.]api.nerdo.solutions/resto
const API_BASE = process.env.REACT_APP_API_URL || '';

// Basename de la SPA (el build vive en /resto/admin). Se deriva del path actual
// igual que en App.js para que el returnUrl absoluto apunte a la ruta correcta.
function getAppBasename() {
  return window.location.pathname.startsWith('/resto/admin') ? '/resto/admin' : '';
}

/**
 * URL de initiate del flujo admin. returnUrl DEBE ser absoluto y first-party
 * (requisito de `isValidAdminReturnUrl` en el backend), por eso usamos
 * `window.location.origin` + basename + `/creating-org`.
 */
function buildInitiateUrl() {
  const returnUrl = `${window.location.origin}${getAppBasename()}/creating-org`;
  const url = new URL(`${API_BASE}/auth/social/google/initiate`);
  url.searchParams.set('flow', 'admin');
  url.searchParams.set('returnUrl', returnUrl);
  return url.toString();
}

// Logo oficial de Google (SVG marca, colores propios del asset).
function GoogleLogo() {
  return (
    <svg
      className="btn-google__icon"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

/**
 * @param {object}   props
 * @param {'register'|'login'} [props.context='register'] — origen para pixel.
 */
function GoogleSignInButton({ context = 'register' }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  // Sin API base configurada no hay flujo posible → no renderizamos nada
  // (defensa; en la práctica REACT_APP_API_URL siempre está seteado).
  if (!API_BASE) return null;

  const handleGoogle = () => {
    setLoading(true);
    // Conversión Lead también para el flujo federado (mismo pixel cross-domain,
    // no-op sin consentimiento). El CompleteRegistration del flujo Google se
    // dispara al finalizar la creación de la organización en CreatingOrgPage.
    if (context === 'register') {
      trackPixel('Lead', {
        content_name: 'resto_admin_register_google',
        content_category: sessionStorage.getItem('preselectedPlan') || 'none',
      });
    }
    // Redirección de página completa al backend → Google. No se ejecuta más código.
    window.location.href = buildInitiateUrl();
  };

  return (
    <div className="google-signin">
      <button
        type="button"
        className="btn btn-google btn-block"
        onClick={handleGoogle}
        disabled={loading}
      >
        <GoogleLogo />
        {loading ? t('auth.googleLoading') : t('auth.continueWithGoogle')}
      </button>

      <div className="auth-divider">
        <span>{t('auth.orUseEmail')}</span>
      </div>
    </div>
  );
}

export default GoogleSignInButton;
